import type { ColumnClassificationMap } from './columnClassificationStore';
import type { DataModel, DataSource, ModelTable, TableColumn } from './dataModelsTypes';
import { columnsForTable, formatBytes } from './dataModelsUtils';

export interface TablePreviewRow {
  cells: string[];
}

export interface TableRelation {
  name: string;
  kind: string;
  field: string;
}

export interface TableActivityItem {
  action: string;
  at: string;
  actor: string;
}

export interface TableDetailData {
  columns: TableColumn[];
  preview: TablePreviewRow[];
  previewHeaders: string[];
  dataSize: string;
  createdOn: string;
  refresh: string;
  tags: string[];
  pii: boolean;
  overview: string;
  domain: string;
  schemaClass: string;
  uniqueEntities: string;
  uniqueLift: string;
  rowLift: string;
  dateRange: string;
  relationships: TableRelation[];
  activity: TableActivityItem[];
  usage: Array<{ label: string; value: string }>;
  docs: string;
}

const FEATURED: Partial<Record<string, Omit<TableDetailData, 'columns' | 'preview' | 'previewHeaders' | 'dataSize'>>> = {
  customer_transactions: {
    createdOn: 'Jan 12, 2024',
    refresh: 'Daily',
    tags: ['Transactions', 'Orders', 'Customer 360'],
    pii: false,
    overview:
      'Contains all customer transaction records including orders, payments, and status information. This table is used for revenue analytics, customer behavior analysis, and financial reporting.',
    domain: 'Customer',
    schemaClass: 'ANALYTICS.PUBLIC',
    uniqueEntities: '1.2M',
    uniqueLift: '+8%',
    rowLift: '+12%',
    dateRange: 'Jan 1, 2022 – Jan 10, 2024',
    relationships: [
      { name: 'customer_profile', kind: 'Table', field: 'customer_id' },
      { name: 'order_items', kind: 'Table', field: 'order_id' },
      { name: 'payment_methods', kind: 'Table', field: 'payment_method' },
      { name: 'refunds', kind: 'Table', field: 'transaction_id' },
    ],
    activity: [
      { action: 'Schema updated', at: '2 days ago', actor: 'John Doe' },
      { action: 'Data scanned', at: '3 days ago', actor: 'System' },
      { action: 'Description updated', at: '5 days ago', actor: 'Sarah Wilson' },
      { action: 'Tag added', at: '1 week ago', actor: 'Michael Chen' },
    ],
    usage: [
      { label: 'Queries this week', value: '1,284' },
      { label: 'Downstream models', value: '6' },
      { label: 'MCP tools', value: '2' },
      { label: 'Active consumers', value: '18' },
    ],
    docs: 'Governed fact table for customer purchases. Join to customer_profile on customer_id and to order_items on order_id. Amounts are stored in the original transaction currency.',
  },
};

const PREVIEW_TXN = [
  ['TXN_0001', 'CUST_12345', 'ORD_78901', '2024-01-10 10:23:45', '125.50', 'USD'],
  ['TXN_0002', 'CUST_67890', 'ORD_78902', '2024-01-10 11:15:12', '89.99', 'USD'],
  ['TXN_0003', 'CUST_11111', 'ORD_78903', '2024-01-10 12:45:33', '299.00', 'USD'],
  ['TXN_0004', 'CUST_22222', 'ORD_78904', '2024-01-10 13:20:18', '45.00', 'EUR'],
  ['TXN_0005', 'CUST_33333', 'ORD_78905', '2024-01-10 14:05:27', '199.99', 'USD'],
];

function hash(value: string): number {
  return Array.from(value).reduce((acc, char) => acc + char.charCodeAt(0), 0);
}

export function buildTableDetail(
  table: ModelTable,
  model: DataModel,
  source?: DataSource,
  overrides?: ColumnClassificationMap,
): TableDetailData {
  const columns = columnsForTable(table, overrides);
  const featured = FEATURED[table.name];
  const headers = columns.slice(0, 6).map((column) => column.name);
  const preview =
    table.name === 'customer_transactions'
      ? PREVIEW_TXN.map((cells) => ({ cells }))
      : Array.from({ length: 5 }, (_, row) => ({
          cells: headers.map((header, col) =>
            col === 0 ? `${header.slice(0, 3).toUpperCase()}_${1000 + row}` : `${header}_${row + 1}`
          ),
        }));

  return {
    columns,
    preview,
    previewHeaders: headers,
    dataSize: formatBytes(table.rows * Math.max(table.columns, 8) * 12),
    createdOn: featured?.createdOn ?? 'Mar 4, 2024',
    refresh: featured?.refresh ?? 'Daily',
    tags: featured?.tags ?? model.tags.slice(0, 3),
    pii: columns.some((column) => column.pii || column.classification === 'PII'),
    overview:
      featured?.overview ??
      `${table.description} Used by ${model.name} for analytics and governed MCP access.`,
    domain: featured?.domain ?? model.tags[0] ?? 'Enterprise',
    schemaClass: featured?.schemaClass ?? `${(source?.kind ?? 'warehouse').toUpperCase()}.PUBLIC`,
    uniqueEntities: featured?.uniqueEntities ?? `${Math.max(12, (hash(table.id) % 90) + 10)}K`,
    uniqueLift: featured?.uniqueLift ?? '+4%',
    rowLift: featured?.rowLift ?? '+7%',
    dateRange: featured?.dateRange ?? 'Jan 1, 2023 – Oct 1, 2026',
    relationships: featured?.relationships ?? [
      { name: model.name.toLowerCase().replace(/\s+/g, '_'), kind: 'Model', field: columns[0]?.name ?? 'id' },
      { name: 'shared_calendar', kind: 'Table', field: 'created_at' },
    ],
    activity: featured?.activity ?? [
      { action: 'Schema refreshed', at: '2 days ago', actor: model.owner },
      { action: 'Data scanned', at: '4 days ago', actor: 'System' },
    ],
    usage: featured?.usage ?? [
      { label: 'Queries this week', value: String(80 + (hash(table.id) % 400)) },
      { label: 'Downstream models', value: '2' },
      { label: 'MCP tools', value: '1' },
      { label: 'Active consumers', value: '6' },
    ],
    docs:
      featured?.docs ??
      `${table.name} is registered on ${model.name}. Source: ${source?.name ?? 'Unknown'}.`,
  };
}
